/**
 * Z chyby dotazu vyrobí vetu, ktorá človeku povie, čo sa deje.
 *
 * Serverové hlášky sú po slovensky a konkrétne ("Projekt nenájdený"), tak sa
 * ukážu. Náhradná hláška z `request()` ("Request failed (500)") je po anglicky
 * a nič nehovorí, tak sa nahradí všeobecnou vetou — inak by v appke svietila
 * angličtina presne vo chvíli, keď je človek už aj tak zmätený.
 *
 * `ApiError` sa rozoznáva podľa tvaru (číselný `status`), nie cez import z
 * lib/api: testy stránok si lib/api bežne celé nahrádzajú atrapou s jediným
 * `api`, a import triedy by im potom zhodil vykreslenie chyby.
 */
export const queryErrorHint = (error: unknown): string => {
  if (error instanceof Error && typeof (error as { status?: unknown }).status === 'number') {
    // Aj výpadok siete (stav 0) má vlastnú slovenskú hlášku, takže prejde tu.
    if (error.message && !/^Request failed/.test(error.message)) return error.message;
  }
  return 'Server teraz neodpovedá. Skús to o chvíľu znova.';
};

/**
 * Chybový stav priamo v obsahu stránky namiesto samotného toastu.
 *
 * Toast zmizne po pár sekundách a za ním ostane prázdna plocha, ktorá vyzerá
 * ako "nič tu nie je" — človek potom nevie, či úlohy naozaj nemá, alebo sa
 * iba nenačítali. Tento blok ostáva na mieste dát, kým sa ich nepodarí načítať.
 *
 *   if (query.isError) return (
 *     <QueryError title="Úlohy sa nepodarilo načítať." error={query.error}
 *       onRetry={query.refetch} retrying={query.isFetching} />
 *   );
 *
 * `role="alert"` je tu zámerne: čítačka obrazovky chybu oznámi hneď, keď sa
 * objaví, a nemusí na ňu človek natrafiť pri prechádzaní stránky.
 */
export const QueryError = ({
  title = 'Nepodarilo sa načítať dáta.',
  error,
  onRetry,
  retrying = false,
  compact = false,
  className = '',
}: {
  title?: string;
  error?: unknown;
  /** Zvyčajne `query.refetch`. Návratová hodnota sa ignoruje. */
  onRetry: () => unknown;
  /** Zvyčajne `query.isFetching` — kým beží nový pokus, tlačidlo sa nedá stlačiť znova. */
  retrying?: boolean;
  /** Menší variant do kariet a bočných panelov. */
  compact?: boolean;
  className?: string;
}) => (
  <div
    role="alert"
    className={`flex flex-col items-center text-center ${
      compact ? 'gap-2 px-4 py-5' : 'gap-3 px-6 py-10'
    } ${className}`}
  >
    <p
      className={`font-semibold text-slate-700 dark:text-slate-200 ${
        compact ? 'text-[14px]' : 'text-[15px]'
      }`}
    >
      {title}
    </p>
    <p className="-mt-1 text-[13px] text-slate-500 dark:text-slate-400">{queryErrorHint(error)}</p>
    <button
      type="button"
      className="btn-secondary mt-1 w-auto"
      disabled={retrying}
      aria-busy={retrying || undefined}
      onClick={() => void onRetry()}
    >
      {retrying ? 'Skúšam…' : 'Skús znova'}
    </button>
  </div>
);
